import {
  IServicingAuditCheckResult,
  IServicingAuditClientRow,
  IServicingAuditCounts,
  IServicingAuditIssue,
  IServicingAuditIssueAcknowledgement,
  IServicingAuditResponse,
  isOpenIssueStatus,
  ServicingAuditCheck,
  ServicingAuditStatus,
} from '../interfaces/servicingAudit'

export const SERVICING_AUDIT_STATUS_RANK: Record<ServicingAuditStatus, number> = {
  [ServicingAuditStatus.Pass]: 0,
  [ServicingAuditStatus.Acknowledged]: 1,
  [ServicingAuditStatus.Warning]: 2,
  [ServicingAuditStatus.Fail]: 3,
}

export const worstServicingAuditStatus = (statuses: ServicingAuditStatus[]) =>
  statuses.reduce(
    (worst, status) =>
      SERVICING_AUDIT_STATUS_RANK[status] > SERVICING_AUDIT_STATUS_RANK[worst] ? status : worst,
    ServicingAuditStatus.Pass,
  )

export const servicingAuditIssueStatus = (issue: IServicingAuditIssue) =>
  issue.acknowledgedAt ? ServicingAuditStatus.Acknowledged : issue.severity

export const rollUpServicingAuditChecks = (
  checks: Record<ServicingAuditCheck, IServicingAuditCheckResult>,
) => worstServicingAuditStatus(Object.values(checks).map(({ status }) => status))

export const countServicingAuditResults = (
  clients: IServicingAuditClientRow[],
): IServicingAuditCounts => ({
  clientsWithIssues: clients.filter((client) => isOpenIssueStatus(client.status)).length,
  totalClients: clients.length,
})

const acknowledgeInClient = (
  client: IServicingAuditClientRow,
  update: IServicingAuditIssueAcknowledgement,
): IServicingAuditClientRow => {
  const checks = Object.fromEntries(
    Object.entries(client.checks).map(([check, result]) => {
      if (!result.issues.some((issue) => issue.issueKey === update.issueKey)) {
        return [check, result]
      }
      const issues = result.issues.map((issue) =>
        issue.issueKey === update.issueKey
          ? {
              ...issue,
              acknowledgedAt: update.acknowledgedAt,
              acknowledgedByName: update.acknowledgedByName,
              acknowledgementNote: update.acknowledgementNote,
            }
          : issue,
      )
      return [
        check,
        {
          ...result,
          issues,
          status: worstServicingAuditStatus(issues.map(servicingAuditIssueStatus)),
        },
      ]
    }),
  ) as Record<ServicingAuditCheck, IServicingAuditCheckResult>
  return { ...client, checks, status: rollUpServicingAuditChecks(checks) }
}

/** Applies a mark-as-intended (or reopen) to a loaded audit so the page reflects it at once. */
export const applyServicingAuditAcknowledgement = (
  audit: IServicingAuditResponse,
  update: IServicingAuditIssueAcknowledgement,
): IServicingAuditResponse => {
  let issuesDelta = 0
  const clients = audit.clients.map((client) => {
    const updated = acknowledgeInClient(client, update)
    issuesDelta +=
      Number(isOpenIssueStatus(updated.status)) - Number(isOpenIssueStatus(client.status))
    return updated
  })
  return {
    ...audit,
    clients,
    counts: { ...audit.counts, clientsWithIssues: audit.counts.clientsWithIssues + issuesDelta },
  }
}
